import {Select as SelectPrimitive} from '@base-ui/react/select'
import type {ReactElement, ReactNode} from 'react'
import {ChevronDownIcon, ChevronUpIcon} from '@/components/ui/icon'
import {cn} from '@utilities/helpers'

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

function SelectValue({className, ...props}: SelectPrimitive.Value.Props) {
    return (
        <SelectPrimitive.Value className={cn('flex flex-1 text-left', className)} data-slot="select-value" {...props} />
    )
}

interface SelectTriggerProps extends Omit<SelectPrimitive.Trigger.Props, 'children'> {
    /** Optional leading icon rendered before the value. */
    icon?: ReactElement
    /** Placeholder shown when no value is selected. */
    placeholder?: ReactNode
    /**
     * The selected value display. Pass a function `(value) => node` to format the
     * selected value (like the old `displayValue`), or a plain node.
     */
    children?: SelectPrimitive.Value.Props['children']
}

const isEmptyValue = (value: unknown) =>
    value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)

/**
 * Trigger styled as a secondary button.
 * Mirrors the old Ariakit `SelectTrigger`: leading icon, placeholder, chevron
 * arrow that rotates when open, and the primary focus ring on hover/focus/open.
 */
function SelectTrigger({className, icon, placeholder, children, ...props}: SelectTriggerProps) {
    return (
        <SelectPrimitive.Trigger
            className={cn(
                // secondary button base
                'group inline-flex h-10 w-fit cursor-pointer select-none items-center justify-between gap-1 rounded-lg bg-white px-5 font-normal text-neutral-900 text-sm shadow-xs outline -outline-offset-1 outline-neutral-300 transition-all',
                // hover / focus / open focus ring (primary)
                'not-data-disabled:hover:bg-neutral-50 not-data-disabled:hover:outline-primary-300 not-data-disabled:hover:shadow-[0_0_0_4px_var(--color-primary-100)] not-data-disabled:focus-visible:outline-primary-300 not-data-disabled:focus-visible:shadow-[0_0_0_4px_var(--color-primary-100)] data-popup-open:outline-primary-300 data-popup-open:shadow-[0_0_0_4px_var(--color-primary-100)]',
                // disabled
                'data-disabled:cursor-not-allowed data-disabled:bg-neutral-100 data-disabled:text-neutral-400',
                '[&_svg]:text-neutral-500',
                className
            )}
            data-slot="select-trigger"
            nativeButton={false}
            {...props}
        >
            {icon}
            <SelectPrimitive.Value
                className="flex flex-1 truncate text-left font-semibold data-placeholder:font-normal data-placeholder:text-neutral-400"
                data-slot="select-value"
            >
                {value => {
                    if (isEmptyValue(value)) return placeholder
                    return typeof children === 'function' ? children(value) : children
                }}
            </SelectPrimitive.Value>
            <SelectPrimitive.Icon className="ml-auto text-neutral-500 [&_svg]:fill-current">
                <ChevronDownIcon
                    className="transition-transform duration-200 ease-in-out in-data-popup-open:rotate-180"
                    size={16}
                />
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
    )
}

function SelectContent({
    className,
    children,
    side = 'bottom',
    sideOffset = 4,
    align = 'start',
    alignOffset = 0,
    alignItemWithTrigger = false,
    ...props
}: SelectPrimitive.Popup.Props &
    Pick<SelectPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'alignItemWithTrigger' | 'side' | 'sideOffset'>) {
    return (
        <SelectPrimitive.Portal>
            <SelectPrimitive.Positioner
                align={align}
                alignItemWithTrigger={alignItemWithTrigger}
                alignOffset={alignOffset}
                className="z-(--z-dropdown) w-(--anchor-width)"
                side={side}
                sideOffset={sideOffset}
            >
                <SelectPrimitive.Popup
                    className={cn(
                        'z-(--z-dropdown) max-h-100 w-full overflow-y-hidden rounded-lg border border-neutral-200 bg-white p-1 px-1.5 shadow-lg',
                        'grid grid-rows-[minmax(0,1fr)]',
                        'origin-(--transform-origin) transition-[transform,scale,opacity] data-ending-style:scale-95 data-starting-style:scale-95 data-ending-style:opacity-0 data-starting-style:opacity-0',
                        className
                    )}
                    data-slot="select-content"
                    {...props}
                >
                    <SelectPrimitive.List className="overflow-y-auto scrollbar">{children}</SelectPrimitive.List>
                </SelectPrimitive.Popup>
            </SelectPrimitive.Positioner>
        </SelectPrimitive.Portal>
    )
}

function SelectLabel({className, ...props}: SelectPrimitive.GroupLabel.Props) {
    return (
        <SelectPrimitive.GroupLabel
            className={cn('px-2 py-1.5 font-medium text-neutral-500 text-xs', className)}
            data-slot="select-label"
            {...props}
        />
    )
}

function SelectItem({className, children, ...props}: SelectPrimitive.Item.Props) {
    return (
        <SelectPrimitive.Item
            className={cn(
                'my-px flex cursor-pointer items-center overflow-x-hidden truncate rounded-md px-2 py-2.5 text-md text-neutral-900 transition-all',
                'data-disabled:pointer-events-none data-disabled:text-neutral-400',
                'data-highlighted:not-data-selected:bg-neutral-50',
                'data-selected:bg-primary-100',
                className
            )}
            data-slot="select-item"
            {...props}
        >
            <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        </SelectPrimitive.Item>
    )
}

function SelectSeparator({className, ...props}: SelectPrimitive.Separator.Props) {
    return (
        <SelectPrimitive.Separator
            className={cn('pointer-events-none -mx-1 my-1 h-px bg-neutral-200', className)}
            data-slot="select-separator"
            {...props}
        />
    )
}

function SelectScrollUpButton({className, ...props}: SelectPrimitive.ScrollUpArrow.Props) {
    return (
        <SelectPrimitive.ScrollUpArrow
            className={cn('top-0 z-10 flex w-full cursor-default items-center justify-center bg-white py-1', className)}
            data-slot="select-scroll-up-button"
            {...props}
        >
            <ChevronUpIcon size={16} />
        </SelectPrimitive.ScrollUpArrow>
    )
}

function SelectScrollDownButton({className, ...props}: SelectPrimitive.ScrollDownArrow.Props) {
    return (
        <SelectPrimitive.ScrollDownArrow
            className={cn(
                'bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-white py-1',
                className
            )}
            data-slot="select-scroll-down-button"
            {...props}
        >
            <ChevronDownIcon size={16} />
        </SelectPrimitive.ScrollDownArrow>
    )
}

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue
}
